/* ShareTargetModal — teacher picks WHO gets control of a shared activity or
 * board collab. Everyone in class always SEES the share; only the selected
 * participants can interact. Lists everyone currently online (presence
 * heartbeat, both enrolled students and guest-link students). */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Check, Loader2, X, Globe, UserCheck, WifiOff, Link2, Copy } from "lucide-react";

export default function ShareTargetModal({
  open, onClose, classId, title = "Share with class",
  subtitle = "Everyone sees it — choose who can interact",
  confirmLabel = "Share now", allowDisable = false, onDisable, onConfirm,
  everyoneDesc = "All participants can interact",
  specificDesc = "Others watch in view-only mode",
  boardLink = "",
}) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("all"); // "all" | "specific"
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("all"); setSelected({}); setLoading(true);
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/participants`);
        if (alive) setParticipants(data.participants || []);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [open, classId]);

  if (!open) return null;

  const chosen = participants.filter((p) => selected[p.participant_id]);
  const allSelected = participants.length > 0 && chosen.length === participants.length;
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(participants.map((p) => [p.participant_id, true])));
  };
  const canConfirm = mode === "all" || chosen.length > 0;

  const confirm = async () => {
    if (!canConfirm || busy) return;
    setBusy(true);
    try {
      if (mode === "all") await onConfirm(["*"], []);
      else await onConfirm(chosen.map((p) => p.participant_id), chosen.map((p) => p.name));
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="share-target-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="w-full max-w-md bg-[#10141C] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-white/10 bg-gradient-to-r from-[#FFD600]/10 to-transparent">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#FFD600] font-bold inline-flex items-center gap-1.5">
                  <Users size={11} /> / share control
                </div>
                <h3 className="font-display font-bold text-xl text-white tracking-tighter mt-1">{title}</h3>
                <p className="text-xs text-neutral-400 mt-1 leading-relaxed">{subtitle}</p>
              </div>
              <button onClick={onClose} data-testid="share-target-close" className="text-white/50 hover:text-white shrink-0 ml-3"><X size={16} /></button>
            </div>
          </div>

          <div className="p-5 space-y-2">
            {/* Live board link — teacher pastes this in the Meet chat; the
                student opens it and lands straight on the live board. */}
            {boardLink && (
              <div className="border border-[#00A48F]/40 bg-[#00A48F]/10 rounded-lg p-3 mb-1" data-testid="board-link-section">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#2DD4BF] font-bold inline-flex items-center gap-1.5 mb-1.5">
                  <Link2 size={11} /> live board link
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-neutral-300 truncate flex-1" data-testid="board-link-url">{boardLink}</span>
                  <button
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(boardLink); toast.success("Board link copied — paste it in the Meet chat"); }
                      catch { toast.error("Copy failed — long-press to copy manually"); }
                    }}
                    data-testid="board-link-copy"
                    className="text-[11px] font-bold px-3 py-1.5 rounded-md bg-[#00A48F] hover:bg-[#00C4A7] text-white shrink-0 inline-flex items-center gap-1 transition-colors"
                  >
                    <Copy size={11} /> Copy
                  </button>
                </div>
                <p className="text-[10px] text-neutral-400 mt-1.5 leading-relaxed">
                  Paste it in the Google Meet chat. The student opens it, types their name and lands straight on this live board — quizzes and activities you share arrive there too, and they appear in the list below so you can let them draw.
                </p>
              </div>
            )}
            {/* Everyone */}
            <button
              onClick={() => setMode("all")}
              data-testid="share-target-everyone"
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors ${mode === "all"
                ? "border-[#FFD600] bg-[#FFD600]/10"
                : "border-white/10 hover:border-white/30"}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mode === "all" ? "bg-[#FFD600] text-[#0A0C10]" : "bg-white/10 text-white/60"}`}>
                <Globe size={15} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">Everyone</div>
                <div className="text-[11px] text-neutral-400">{everyoneDesc}</div>
              </div>
              {mode === "all" && <Check size={16} className="text-[#FFD600]" />}
            </button>

            {/* Specific */}
            <button
              onClick={() => setMode("specific")}
              data-testid="share-target-specific"
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-colors ${mode === "specific"
                ? "border-[#22D3EE] bg-[#22D3EE]/10"
                : "border-white/10 hover:border-white/30"}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mode === "specific" ? "bg-[#22D3EE] text-[#0A0C10]" : "bg-white/10 text-white/60"}`}>
                <UserCheck size={15} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">Specific participants</div>
                <div className="text-[11px] text-neutral-400">{specificDesc}</div>
              </div>
              {mode === "specific" && <Check size={16} className="text-[#22D3EE]" />}
            </button>

            {mode === "specific" && (
              <div className="border border-white/10 rounded-lg overflow-hidden mt-1" data-testid="share-target-list">
                <div className="flex items-center justify-between px-3 py-2 bg-white/[0.04] border-b border-white/10">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/50">
                    Online now · {participants.length}
                  </span>
                  {participants.length > 0 && (
                    <button onClick={toggleAll} data-testid="share-target-select-all" className="text-[11px] font-semibold text-[#22D3EE] hover:text-white">
                      {allSelected ? "Clear all" : "Select all"}
                    </button>
                  )}
                </div>
                <div className="max-h-[220px] overflow-y-auto divide-y divide-white/[0.06]">
                  {loading ? (
                    <div className="py-8 text-center text-white/40"><Loader2 size={16} className="animate-spin mx-auto" /></div>
                  ) : participants.length === 0 ? (
                    <div className="py-8 text-center text-white/40 text-xs px-4">
                      <WifiOff size={16} className="mx-auto mb-2 opacity-50" />
                      No students online yet. Students appear here once they have the <strong>classroom page</strong> open — being in the Google Meet alone isn't enough. Enrolled students: dashboard → Join. Guests: send them your invite link.
                    </div>
                  ) : participants.map((p) => {
                    const on = !!selected[p.participant_id];
                    return (
                      <button
                        key={p.participant_id}
                        onClick={() => setSelected((s) => ({ ...s, [p.participant_id]: !s[p.participant_id] }))}
                        data-testid={`share-target-student-${p.participant_id}`}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.04] text-left transition-colors"
                      >
                        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${on ? "bg-[#22D3EE] border-[#22D3EE]" : "border-white/25"}`}>
                          {on && <Check size={12} className="text-[#0A0C10]" />}
                        </span>
                        <span className="w-7 h-7 rounded-full bg-[#FFD600]/15 text-[#FFD600] flex items-center justify-center font-bold text-[11px] shrink-0">
                          {(p.name || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-white font-medium truncate">{p.name}</span>
                        </span>
                        {p.is_guest && (
                          <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white/10 text-white/50">guest</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-white/10 flex items-center gap-2">
            {allowDisable && (
              <button
                onClick={onDisable}
                data-testid="share-target-disable"
                className="text-xs px-3 py-2.5 rounded-md border border-red-400/40 text-red-300 hover:bg-red-500/15 font-semibold"
              >
                Turn off
              </button>
            )}
            <div className="flex-1" />
            <button onClick={onClose} className="text-sm px-4 py-2.5 rounded-md border border-white/15 text-neutral-300 hover:border-white/40">
              Cancel
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); confirm(); }}
              disabled={!canConfirm || busy}
              data-testid="share-target-confirm"
              className="text-sm px-5 py-2.5 rounded-md bg-[#FFD600] text-[#0A0C10] font-bold hover:bg-[#FFC600] disabled:opacity-40 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {confirmLabel}{mode === "specific" && chosen.length > 0 ? ` · ${chosen.length}` : ""}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
